// A token deleted from the board leaves its combatant in the turn order (issue 1537): by design, so the undelete
// puts the token back on its turn (`encounter.test.ts`, "a deleted token keeps its link"). In a playtest the DM could
// not find how to take the leftovers out, and they took a turn every round of the next fight. So a delete that leaves
// combatants with no token on the board says so to whoever runs the turn order, with a button that removes them. The
// button is the DM's choice: pressing it is an action, which ends the undelete as any action does.
import { useCallback, useRef } from 'react';
import { emptyEncounter, type Encounter } from '@dragons-table/vtt-shared';
import { useTableState } from './TableState';
import { useMayAct } from './mayAct';

/** The combatants that had a token on the board and would have none once `removed` leave it, in the order's order. */
export function strandedBy(enc: Pick<Encounter, 'combatants'>, objects: Record<string, unknown>, removed: readonly string[]): Array<{ id: string; name: string }> {
  const gone = new Set(removed);
  return enc.combatants
    .filter((c) => c.tokens.some((id) => objects[id]) && !c.tokens.some((id) => objects[id] && !gone.has(id)))
    .map((c) => ({ id: c.id, name: c.name }));
}

/** "Goblin is still in the turn order." / "Goblin and Orc are …" / "Goblin, Orc and 2 others are …" */
export function strandedNotice(names: readonly string[]): string {
  if (names.length === 1) return `${names[0]} is still in the turn order.`;
  const list = names.length === 2 ? `${names[0]} and ${names[1]}`
    : names.length === 3 ? `${names[0]}, ${names[1]} and ${names[2]}`
    : `${names[0]}, ${names[1]} and ${names.length - 2} others`;
  return `${list} are still in the turn order.`;
}

export const REMOVE_FROM_ORDER = 'Remove from turn order';

/**
 * Removes objects from the board as one `object.remove` (one undelete), and, when that strands combatants and this
 * viewer runs the turn order, offers on the board's notice to remove them from it too. A player never sees it.
 */
export function useRemoveObjects(): (ids: string[]) => void {
  const { store, role, act, setNotice } = useTableState();
  const mayAct = useMayAct();
  const runsOrder = role === 'dm' && mayAct('encounter.remove');
  // The store as it is when the button is pressed: the notice stays a minute, and an undelete in it puts tokens back.
  const now = useRef(store);
  now.current = store;
  return useCallback((ids: string[]) => {
    const enc = (store.state.encounter ?? emptyEncounter()) as Encounter;
    const stranded = runsOrder ? strandedBy(enc, store.state.objects as Record<string, unknown>, ids) : [];
    act({ kind: 'object.remove', ids }, (r) => {
      if (!r.ok || stranded.length === 0) return;
      setNotice(strandedNotice(stranded.map((c) => c.name)), { label: REMOVE_FROM_ORDER, run: () => {
        // Only those still in the order with no token on the board: one whose token was undeleted since stays.
        const { objects, encounter } = now.current.state;
        const still = stranded.filter((s) => (encounter ?? emptyEncounter()).combatants.some((c) => c.id === s.id && !c.tokens.some((t) => objects[t])));
        if (still.length) act({ kind: 'encounter.remove', ids: still.map((c) => c.id) });
      } });
    });
  }, [store, runsOrder, act, setNotice]);
}
